(() => {
  if (window.NailColorPickerInitialized) return;
  window.NailColorPickerInitialized = true;

  const pickerSelector = '[data-nail-color-picker]';
  const states = new Map();
  const swipeThreshold = 45;
  const swipeAxisRatio = 1.2;

  const normalize = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  const getKey = (picker) => picker.dataset.nailPickerKey || window.location.pathname;

  const getState = (picker) => {
    const key = getKey(picker);
    if (!states.has(key)) states.set(key, { expanded: false, query: '' });
    return states.get(key);
  };

  const getProductInfo = (picker) => picker?.closest('product-info') || null;
  const getGallery = (picker) => getProductInfo(picker)?.querySelector('media-gallery') || null;
  const getViewer = (picker) => getGallery(picker)?.querySelector('[id^="GalleryViewer"]') || null;
  const getPickerFromViewer = (viewer) => viewer.closest('product-info')?.querySelector(pickerSelector) || null;

  const getAllVariantInputs = (picker) =>
    Array.from(picker.querySelectorAll('[data-nail-item] input[type="radio"]'));

  const getVariantInputs = (picker) => {
    const inputs = getAllVariantInputs(picker);
    const search = picker.querySelector('[data-nail-search]');
    if (!search || !normalize(search.value)) return inputs;
    return inputs.filter((input) => !input.closest('[data-nail-item]')?.hidden);
  };

  const getLargeImageUrl = (source) => {
    if (!source) return '';
    try {
      const url = new URL(source, window.location.href);
      url.searchParams.set('width', '1946');
      return url.href;
    } catch (_error) {
      return source.replace(/([?&]width=)\d+/i, '$11946');
    }
  };

  const getCurrentIndex = (picker, inputs = getAllVariantInputs(picker)) => {
    let index = inputs.findIndex((input) => input.checked);
    return index < 0 ? 0 : index;
  };

  const makeChevron = (direction) => {
    const span = document.createElement('span');
    span.setAttribute('aria-hidden', 'true');
    span.textContent = direction < 0 ? '‹' : '›';
    span.style.fontSize = '3rem';
    span.style.fontWeight = '300';
    span.style.lineHeight = '1';
    return span;
  };

  const updateVariantNavigation = (picker) => {
    const nav = getProductInfo(picker)?.querySelector('[data-nail-variant-navigation]');
    if (!nav) return;
    const inputs = getAllVariantInputs(picker);
    const current = getCurrentIndex(picker, inputs);
    const counter = nav.querySelector('[data-nail-variant-counter]');
    if (counter) counter.textContent = `${Math.min(current + 1, inputs.length)}/${inputs.length}`;
    nav.hidden = inputs.length < 2;
  };

  const ensureVariantNavigation = (picker) => {
    const gallery = getGallery(picker);
    const viewer = getViewer(picker);
    const productInfo = getProductInfo(picker);
    if (!gallery || !viewer || !productInfo) return;

    const existingCustom = productInfo.querySelector('[data-nail-variant-navigation]');
    if (existingCustom) {
      updateVariantNavigation(picker);
      return;
    }

    const inputs = getAllVariantInputs(picker);
    if (inputs.length < 2) return;

    const nav = document.createElement('div');
    nav.dataset.nailVariantNavigation = 'true';
    nav.setAttribute('aria-label', 'Color navigation');
    nav.style.display = 'flex';
    nav.style.alignItems = 'center';
    nav.style.justifyContent = 'center';
    nav.style.gap = '3.2rem';
    nav.style.width = '100%';
    nav.style.padding = '1.4rem 0 1.8rem';
    nav.style.color = 'rgb(var(--color-foreground))';

    const previous = document.createElement('button');
    previous.type = 'button';
    previous.setAttribute('aria-label', 'Previous color');
    previous.style.display = 'grid';
    previous.style.placeItems = 'center';
    previous.style.width = '4.4rem';
    previous.style.height = '4.4rem';
    previous.style.padding = '0';
    previous.style.border = '0';
    previous.style.background = 'transparent';
    previous.style.color = 'inherit';
    previous.style.cursor = 'pointer';
    previous.appendChild(makeChevron(-1));

    const counter = document.createElement('span');
    counter.dataset.nailVariantCounter = 'true';
    counter.style.minWidth = '5.6rem';
    counter.style.fontSize = '1.6rem';
    counter.style.textAlign = 'center';

    const next = document.createElement('button');
    next.type = 'button';
    next.setAttribute('aria-label', 'Next color');
    next.style.display = 'grid';
    next.style.placeItems = 'center';
    next.style.width = '4.4rem';
    next.style.height = '4.4rem';
    next.style.padding = '0';
    next.style.border = '0';
    next.style.background = 'transparent';
    next.style.color = 'inherit';
    next.style.cursor = 'pointer';
    next.appendChild(makeChevron(1));

    previous.addEventListener('click', () => selectAdjacentColor(viewer, -1, true));
    next.addEventListener('click', () => selectAdjacentColor(viewer, 1, true));

    nav.append(previous, counter, next);
    viewer.insertAdjacentElement('afterend', nav);
    updateVariantNavigation(picker);
  };

  const previewSelectedImage = (input) => {
    const picker = input.closest(pickerSelector);
    const gallery = getGallery(picker);
    const label = input.labels?.[0];
    const thumbnail = label?.querySelector('.nail-color-picker__image');
    const source = thumbnail?.currentSrc || thumbnail?.src;
    const imageUrl = getLargeImageUrl(source);
    if (!gallery || !imageUrl) return;

    const activeImage =
      gallery.querySelector('.product__media-item.is-active .product__media img') ||
      gallery.querySelector('.product__media img');
    if (!activeImage) return;

    activeImage.removeAttribute('srcset');
    activeImage.removeAttribute('sizes');
    activeImage.alt = input.value || label?.title || activeImage.alt;
    activeImage.src = imageUrl;
    updateVariantNavigation(picker);
  };

  function selectAdjacentColor(viewer, direction, useAllInputs = false) {
    const picker = getPickerFromViewer(viewer);
    if (!picker) return;
    const inputs = useAllInputs ? getAllVariantInputs(picker) : getVariantInputs(picker);
    if (inputs.length < 2) return;

    let currentIndex = inputs.findIndex((input) => input.checked);
    if (currentIndex < 0) currentIndex = 0;
    const nextIndex = (currentIndex + direction + inputs.length) % inputs.length;
    const nextInput = inputs[nextIndex];
    if (!nextInput || nextInput === inputs[currentIndex]) return;
    nextInput.click();
  }

  const bindSwipe = (picker) => {
    const viewer = getViewer(picker);
    if (!viewer || viewer.dataset.nailVariantSwipeBound === 'true') return;

    viewer.dataset.nailVariantSwipeBound = 'true';
    viewer.classList.add('nail-color-picker__variant-swipe');
    viewer.setAttribute('aria-roledescription', 'color carousel');
    viewer.style.touchAction = 'pan-y pinch-zoom';
    viewer.style.overscrollBehaviorX = 'contain';
    viewer.style.webkitUserSelect = 'none';
    viewer.style.userSelect = 'none';

    let startX = null;
    let startY = null;
    let suppressClickUntil = 0;

    const resetTouch = () => {
      startX = null;
      startY = null;
    };

    viewer.addEventListener('touchstart', (event) => {
      if (event.touches.length !== 1) {
        resetTouch();
        return;
      }
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
    }, { passive: true });

    viewer.addEventListener('touchend', (event) => {
      if (startX === null || startY === null || event.changedTouches.length !== 1) {
        resetTouch();
        return;
      }
      const deltaX = event.changedTouches[0].clientX - startX;
      const deltaY = event.changedTouches[0].clientY - startY;
      resetTouch();
      if (Math.abs(deltaX) < swipeThreshold || Math.abs(deltaX) < Math.abs(deltaY) * swipeAxisRatio) return;
      event.preventDefault();
      event.stopPropagation();
      suppressClickUntil = Date.now() + 500;
      selectAdjacentColor(viewer, deltaX < 0 ? 1 : -1, true);
    }, { passive: false });

    viewer.addEventListener('click', (event) => {
      if (Date.now() >= suppressClickUntil) return;
      event.preventDefault();
      event.stopPropagation();
    }, true);

    viewer.addEventListener('touchcancel', resetTouch, { passive: true });
  };

  const applyState = (picker) => {
    if (!picker) return;
    const state = getState(picker);
    const search = picker.querySelector('[data-nail-search]');
    const items = Array.from(picker.querySelectorAll('[data-nail-item]'));
    const toggle = picker.querySelector('[data-nail-toggle]');
    const count = picker.querySelector('[data-nail-count]');
    const limit = Number.parseInt(picker.dataset.initialCount || '16', 10);

    if (search && search.value !== state.query) search.value = state.query;
    const query = normalize(state.query);
    let matches = 0;

    items.forEach((item, index) => {
      const itemText = normalize(item.dataset.nailSearch || item.textContent);
      const matchesQuery = !query || itemText.includes(query);
      const isSelected = Boolean(item.querySelector('input[type="radio"]:checked'));
      const shouldShow = matchesQuery && (Boolean(query) || state.expanded || index < limit || isSelected);
      if (matchesQuery) matches += 1;
      item.hidden = !shouldShow;
    });

    if (count) count.textContent = query ? matches + (matches === 1 ? ' color found' : ' colors found') : '';
    if (toggle) {
      toggle.hidden = Boolean(query) || items.length <= limit;
      toggle.setAttribute('aria-expanded', String(state.expanded));
      toggle.textContent = state.expanded ? 'Show fewer colors' : 'View all ' + items.length + ' colors';
    }

    bindSwipe(picker);
    ensureVariantNavigation(picker);
    updateVariantNavigation(picker);
  };

  const initialize = (root) => {
    if (root.matches && root.matches(pickerSelector)) applyState(root);
    if (root.querySelectorAll) root.querySelectorAll(pickerSelector).forEach(applyState);
  };

  document.addEventListener('change', (event) => {
    if (event.target.matches && event.target.matches('[data-nail-search]')) {
      event.stopPropagation();
      return;
    }
    const input = event.target.closest?.(`${pickerSelector} input[type="radio"]`);
    if (!input) return;
    previewSelectedImage(input);
    applyState(input.closest(pickerSelector));
  }, true);

  document.addEventListener('input', (event) => {
    const search = event.target.closest && event.target.closest('[data-nail-search]');
    if (!search) return;
    const picker = search.closest(pickerSelector);
    if (!picker) return;
    const state = getState(picker);
    state.query = search.value;
    applyState(picker);
  });

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest && event.target.closest('[data-nail-toggle]');
    if (!toggle) return;
    const picker = toggle.closest(pickerSelector);
    if (!picker) return;
    event.preventDefault();
    const state = getState(picker);
    state.expanded = !state.expanded;
    applyState(picker);
  });

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) initialize(node);
      });
    });
  });

  const start = () => {
    initialize(document);
    observer.observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
